"use client";

import { createContext, useContext, useMemo, type ComponentType, type ReactNode } from "react";
import { createFormatters, type BookingFormatters } from "./format";
import { createBookingTranslator, type BookingTranslator } from "./i18n";

export type BookingLinkProps = {
  href: string;
  className?: string;
  children: ReactNode;
  "aria-label"?: string;
  "aria-disabled"?: boolean;
  scroll?: boolean;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

/** The host app's link: next/link on the hosted site, a view switch in the widget. */
export type BookingLink = ComponentType<BookingLinkProps>;

type BookingContextValue = {
  locale: string;
  timeZone: string;
  currency: string;
  t: BookingTranslator;
  fmt: BookingFormatters;
  Link: BookingLink;
};

const BookingContext = createContext<BookingContextValue | null>(null);

function PlainLink({ scroll: _scroll, ...props }: BookingLinkProps) {
  return <a {...props} />;
}

export function BookingProvider({
  locale,
  timeZone,
  currency,
  Link = PlainLink,
  children,
}: {
  locale: string;
  timeZone: string;
  currency: string;
  Link?: BookingLink;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({
      locale,
      timeZone,
      currency,
      t: createBookingTranslator(locale),
      fmt: createFormatters(locale, timeZone, currency),
      Link,
    }),
    [locale, timeZone, currency, Link],
  );

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

export function useBooking(): BookingContextValue {
  const value = useContext(BookingContext);
  if (!value) throw new Error("useBooking must be used inside <BookingProvider>");
  return value;
}
